import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  useMediaQuery,
  styled,
  Grid,
  MenuItem,
  Menu,
  Popper,
  Paper,
  ClickAwayListener,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/reeflogo.jpg";
import { Link } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { path } from "framer-motion/client";

const LogoWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  transition: "all 0.3s ease-in-out",
  marginLeft: 50,

  [theme.breakpoints.down("sm")]: {
    marginLeft: 10,
  },
}));

const LogoImage = styled("img")(({ theme, scrolled }) => ({
  transition: "all 0.3s ease-in-out",
  height: scrolled ? 60 : 180,
  marginTop: scrolled ? 0 : 40,

  [theme.breakpoints.only("xs")]: {
    height: scrolled ? 45 : 110,
    marginTop: scrolled ? 0 : 20,
  },

  [theme.breakpoints.only("md")]: {
    height: scrolled ? 50 : 180,
    marginTop: scrolled ? 0 : 40,
  },
}));

const Header = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [scrolled, setScrolled] = useState(false);
  const [programmeAnchorEl, setProgrammeAnchorEl] = useState(null);
  const [publicationAnchorEl, setPublicationAnchorEl] = useState(null);
  const [peopleAnchorEl, setPeopleAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openProgrammeMobile, setOpenProgrammeMobile] = useState(false);
  const [openPublicationMobile, setOpenPublicationMobile] = useState(false);
  const [openPeopleMobile, setOpenPeopleMobile] = useState(false);

  const handleScroll = () => {
    setScrolled(window.scrollY > 20);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Programme Menu Items
  const programmeItems = [
    {
      label: "Bio Diversity Documentation",
      path: "/biodiversity-of-lakshadweep",
    },
    { label: "Coral Reef Restoration", path: "/restoring-hope" },
    { label: "Community and Resource Governance", path: "/community-resource" },
    { label: "Community wellbeing and Environmental", path: "/community-welbeing" },
    { label: "Sustainable Fisheries", path: "/sustainable-fisheries" },
  ];

  const publicationItems = [
    {
      label: "Reports",
      path: "/report",
      subitems: [
        // { label: "Team", path: "/publication/annual-report-2023" },
        // { label: "Advisors", path: "/publication/research-findings" },
      ],
    },
    {
      label: "⁠Scientific Papers",
      subitems: [
        { label: "Journal Article", path: "/journal-article" },
        { label: "Editorial", path: "/editorial" },
        { label: "Popular Article", path: "/popular-article" },
        { label: "Opinion and Commentaries", path: "/opinionand-commentaries" },
        { label: "Book (Children's Magazine and Newsletter)", path: "/book" },
      ],
    },
    {
      label: " ⁠Outreach Materials",
      subitems: [
        { label: "Education Outreach", path: "/education-outreach" },
        { label: "Field Guide", path: "/field-guide" },
        { label: "Modules and Guide Book for Practitioners", path: "/modules-and-guide" },
        { label: "Future Calendar Series", path: "/future-calendar" },
        { label: "Documentary", path: "/documentary" },
      ],
    },
  ];

  // People Menu Items
  const peopleItems = [
    { label: "Founder Member", path: "/founder" },
    { label: "Administrative Member ", path: "/administrative" },
    { label: "Project Member", path: "/project-members" },
    // { label: "Advisiors Member", path: "" },
    { label: "Advisory Board Members", path: "/advisory-board-members" },

    // { label: "Partners", path: "/partners" },
    // { label: "Volunteers", path: "/volunteers" },
  ];

  // Programme menu handlers
  const handleProgrammeClick = (event) => {
    setProgrammeAnchorEl(programmeAnchorEl ? null : event.currentTarget);
  };

  const handleProgrammeClose = () => {
    setProgrammeAnchorEl(null);
  };

  const handleProgrammeItemClick = (path) => {
    navigate(path);
    handleProgrammeClose();
  };

  // Publication menu handlers
  const handlePublicationClick = (event) => {
    setPublicationAnchorEl(publicationAnchorEl ? null : event.currentTarget);
  };

  const handlePublicationClose = () => {
    setPublicationAnchorEl(null);
  };

  const handlePublicationItemClick = (path) => {
    if (path) {
      navigate(path);
      handlePublicationClose();
    }
  };

  // People menu handlers
  const handlePeopleClick = (event) => {
    setPeopleAnchorEl(peopleAnchorEl ? null : event.currentTarget);
  };

  const handlePeopleClose = () => {
    setPeopleAnchorEl(null);
  };

  const handlePeopleItemClick = (path) => {
    navigate(path);
    handlePeopleClose();
  };

  // Mobile drawer handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileItemClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const Ripple = styled("span")(({ theme, delay = 0 }) => ({
    position: "absolute",
    top: 140,
    left: 137,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    transform: "translate(-50%, -50%)",
    animation: `heroripple 8s ${delay}s linear infinite`,
    zIndex: -1,
    pointerEvents: "none",
    width: "60px",
    height: "60px",

    [theme.breakpoints.down("xs")]: {
      left: 20,
    },

    [theme.breakpoints.down("sm")]: {
      left: 70,
      top: 70,
    },

    [theme.breakpoints.up("md")]: {
      width: "80px",
      height: "80px",
    },

    "@keyframes heroripple": {
      "0%": {
        transform: "translate(-50%, -50%) scale(2)",
        opacity: 0.4,
      },
      "100%": {
        transform: "translate(-50%, -50%) scale(4)",
        opacity: 0,
      },
    },
  }));

  const drawer = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Drawer Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3,
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Menu
        </Typography>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Drawer Content */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        <List>
          {/* Home */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/")}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Home"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Programme Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenProgrammeMobile(!openProgrammeMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Programme"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openProgrammeMobile ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </ListItem>
          <Collapse in={openProgrammeMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {programmeItems.map((item, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleMobileItemClick(item.path)}
                  sx={{
                    pl: 3,
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Publication Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenPublicationMobile(!openPublicationMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Publication"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openPublicationMobile ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </ListItem>
          <Collapse in={openPublicationMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {publicationItems.map((item, index) => (
                <div key={index}>
                  <ListItem
                    button
                    onClick={() => item.path && handleMobileItemClick(item.path)}
                    sx={{
                      pl: 3,
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      }
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  {item.subitems && item.subitems.length > 0 && (
                    <List component="div" disablePadding>
                      {item.subitems.map((subitem, subIndex) => (
                        <ListItem
                          key={subIndex}
                          button
                          onClick={() => handleMobileItemClick(subitem.path)}
                          sx={{
                            pl: 5,
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            }
                          }}
                        >
                          <ListItemText
                            primary={subitem.label}
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </div>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* People Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenPeopleMobile(!openPeopleMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="People"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openPeopleMobile ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </ListItem>
          <Collapse in={openPeopleMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {peopleItems.map((item, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleMobileItemClick(item.path)}
                  sx={{
                    pl: 3,
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Blog */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/blog-page")}
            sx={{
              borderRadius: 1,
              cursor: "pointer",
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Blog"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Gallery */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/gallery")}
            sx={{
              borderRadius: 1,
              cursor: "pointer",
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Gallery"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />


          {/* Learn at REEF */}
          <ListItem
            component="div"
            onClick={() => {
              window.location.href = "https://web.classplusapp.com/login?orgCode=akqmvx";
            }}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              cursor: "pointer",
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Learn at REEF"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Donate */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/donate")}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            <ListItemText
              primary="Donate"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(0, 0, 0, 0.47)" : "transparent",
          transition: "background-color 0.3s ease",
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            [theme.breakpoints.down("sm")]: {
              height: scrolled ? 80 : 120,
            },
            [theme.breakpoints.up("md")]: {
              height: scrolled ? 80 : 220,
            },
          }}
        >
          <Grid container display={"flex"} alignItems={"center"}>
            <Grid item md={2} xs={9}>
              <Link to="/" style={{ textDecoration: "none" }}>
                <LogoWrapper sx={{ display: "flex", alignItems: "center" }}>
                  {!scrolled && !isMobile &&
                    [0, 1, 2].map((_, i) => <Ripple key={i} delay={i} />)}
                  <LogoImage src={logo} alt="Logo" scrolled={scrolled ? 1 : 0} />
                </LogoWrapper>
              </Link>
            </Grid>

            {!isMobile ? (
              <Grid item md={10} xs={9.5}>
                {scrolled && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 2, md: 3 },
                      alignItems: "center",
                      justifyContent: "space-around",
                      color: "#fff",
                      flexGrow: 1,
                      fontSize: {
                        xs: "0.6rem",
                        md: "1rem",
                      },
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Programme Dropdown */}
                    <Box
                      onClick={handleProgrammeClick}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        position: "relative",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Programme
                      {programmeAnchorEl ? (
                        <KeyboardArrowUpIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      )}
                      <Popper
                        open={Boolean(programmeAnchorEl)}
                        anchorEl={programmeAnchorEl}
                        placement="bottom-start"
                        sx={{
                          zIndex: 1300,
                          pt: 1,
                        }}
                      >
                        <ClickAwayListener onClickAway={handleProgrammeClose}>
                          <Paper
                            component={motion.div}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            sx={{
                              width: { xs: "220px", sm: "250px" },
                              maxHeight: { xs: "60vh", sm: "300px" },
                              overflowY: "auto",
                              borderRadius: "12px",
                              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#ffffff",
                              p: 0.5,
                            }}
                          >
                            {programmeItems.map((item, index) => (
                              <MenuItem
                                key={index}
                                onClick={() => handleProgrammeItemClick(item.path)}
                                sx={{
                                  fontSize: { xs: "13px", sm: "14px" },
                                  fontWeight: 500,
                                  py: 1,
                                  px: 1.5,
                                  my: 0.5,
                                  minHeight: "40px",
                                  borderRadius: "8px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                  fontFamily: "Montserrat, sans-serif",
                                  transition: "all 0.2s ease-in-out",
                                  "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                    pl: 2,
                                  },
                                }}
                              >
                                {item.label}
                              </MenuItem>
                            ))}
                          </Paper>
                        </ClickAwayListener>
                      </Popper>
                    </Box>

                    {/* Publication Dropdown */}
                    <Box
                      onClick={handlePublicationClick}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        position: "relative",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Publication
                      {publicationAnchorEl ? (
                        <KeyboardArrowUpIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      )}
                      <Menu
                        anchorEl={publicationAnchorEl}
                        open={Boolean(publicationAnchorEl)}
                        onClose={handlePublicationClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{
                          component: motion.div,
                          initial: { opacity: 0, y: -10 },
                          animate: { opacity: 1, y: 0 },
                          exit: { opacity: 0, y: -10 },
                          transition: { duration: 0.2 },
                          sx: {
                            width: { xs: "220px", sm: "230px" },
                            maxHeight: { xs: "60vh", sm: "300px" },
                            overflowY: "auto",
                            borderRadius: "12px",
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                            backgroundColor: "#ffffff",
                            p: 0.5,
                            ml: -2
                          },
                        }}
                      >
                        {publicationItems.map((item, index) => (
                          <div key={index}>
                            <MenuItem
                              onClick={() => {
                                if (item.subitems.length === 0) {
                                  handlePublicationItemClick(item.path);
                                }
                              }}
                              sx={{
                                fontSize: { xs: "13px", sm: "14px" },
                                fontWeight: 700,
                                py: 1,
                                px: 1.5,
                                my: 0.5,
                                minHeight: "40px",
                                borderRadius: "8px",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                fontFamily: "Montserrat, sans-serif",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                  pl: 2,
                                },
                              }}
                            >
                              {item.label}
                            </MenuItem>
                            {item.subitems.length > 0 && (
                              <Box sx={{ pl: 2 }}>
                                {item.subitems.map((subitem, subIndex) => (
                                  <MenuItem
                                    key={subIndex}
                                    onClick={() => handlePublicationItemClick(subitem.path)}
                                    sx={{
                                      fontSize: { xs: "12px", sm: "13px" },
                                      fontWeight: 400,
                                      py: 1,
                                      px: 1.5,
                                      my: 0.5,
                                      minHeight: "35px",
                                      borderRadius: "8px",
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                      fontFamily: "Montserrat, sans-serif",
                                      transition: "all 0.2s ease-in-out",
                                      position: "relative",
                                      pl: 3,
                                      "&:hover": {
                                        backgroundColor: "#f5f5f5",
                                        pl: 3.5,
                                      },
                                      "&::before": {
                                        content: '"•"',
                                        position: "absolute",
                                        left: "10px",
                                        color: "#555",
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      },
                                    }}
                                  >
                                    {subitem.label}
                                  </MenuItem>
                                ))}
                              </Box>
                            )}
                          </div>
                        ))}
                      </Menu>
                    </Box>

                    {/* People Dropdown */}
                    <Box
                      onClick={handlePeopleClick}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        position: "relative",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      People
                      {peopleAnchorEl ? (
                        <KeyboardArrowUpIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                      )}
                      <Popper
                        open={Boolean(peopleAnchorEl)}
                        anchorEl={peopleAnchorEl}
                        placement="bottom-start"
                        sx={{
                          zIndex: 1300,
                          pt: 1,
                        }}
                      >
                        <ClickAwayListener onClickAway={handlePeopleClose}>
                          <Paper
                            component={motion.div}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            sx={{
                              width: { xs: "180px", sm: "200px" },
                              maxHeight: { xs: "60vh", sm: "300px" },
                              overflowY: "auto",
                              borderRadius: "12px",
                              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#ffffff",
                              p: 0.5,
                            }}
                          >
                            {peopleItems.map((item, index) => (
                              <MenuItem
                                key={index}
                                onClick={() => handlePeopleItemClick(item.path)}
                                sx={{
                                  fontSize: { xs: "13px", sm: "14px" },
                                  fontWeight: 500,
                                  py: 1,
                                  px: 1.5,
                                  my: 0.5,
                                  minHeight: "40px",
                                  borderRadius: "8px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                  fontFamily: "Montserrat, sans-serif",
                                  transition: "all 0.2s ease-in-out",
                                  "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                    pl: 2,
                                  },
                                }}
                              >
                                {item.label}
                              </MenuItem>
                            ))}
                          </Paper>
                        </ClickAwayListener>
                      </Popper>
                    </Box>

                    {/* Blog */}
                    <Box
                      onClick={() => navigate("/blog-page")}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Blog
                    </Box>

                    {/* Gallery */}
                    <Box
                      onClick={() => navigate("/gallery")}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Gallery
                    </Box>

                    <Box
                      onClick={() => {
                        window.location.href = "https://web.classplusapp.com/login?orgCode=akqmvx";
                      }}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        cursor: "pointer",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      Learn at REEF
                    </Box>


                    {/* Donate Button */}
                    <Button
                      onClick={() => navigate("/donate")}
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        border: "1px solid #fff",
                        fontSize: {
                          xs: "0.4rem",
                          md: "0.8rem",
                        },
                        borderRadius: "30px",
                        padding: { md: "1px 20px", xs: "1px 15px" },
                        textTransform: "uppercase",
                        fontWeight: 700,
                        fontFamily: "Montserrat, sans-serif",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        letterSpacing: "0.2em",
                        "&:hover": {
                          backgroundColor: "#111",
                        },
                      }}
                    >
                      Donate
                    </Button>
                  </Box>
                )}
              </Grid>
            ) : (
              <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: '#fff',
                    mr: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: '350px' },
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;